import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MapPin, Clock, Utensils, Wine, Sun, Building2, 
  Plane, Heart, TrendingUp, ArrowRight, ChevronRight,
  Globe, Users, Sparkles, Camera
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { getActiveCountries, getActiveDestinations, getCountryInfo } from '../services/api';

// Comprehensive destination data with lifestyle content
const DESTINATIONS = {
  'greece': {
    id: 'greece',
    name: 'Greece',
    tagline: 'Where Ancient History Meets Mediterranean Bliss',
    heroImage: 'https://images.pexels.com/photos/1285625/pexels-photo-1285625.jpeg',
    region: 'Europe',
    climate: 'Mediterranean',
    timezone: 'GMT+2',
    flightFromIndia: '8-10 hours',
    bestTime: 'April - October',
    language: 'Greek, English widely spoken',
    currency: 'Euro (€)',
    indianCommunity: 'Growing expat community in Athens',
    
    intro: `Greece offers Indian investors a unique blend of ancient civilization, stunning landscapes, and one of Europe's most attractive Golden Visa programs. From the iconic white-washed buildings of Santorini to the cosmopolitan energy of Athens, Greece provides an idyllic setting for those seeking a European lifestyle with Mediterranean warmth.`,
    
    whyInvest: [
      { title: 'Golden Visa from €250,000', desc: 'One of Europe\'s most affordable residency-by-investment programs' },
      { title: 'Schengen Access', desc: 'Visa-free travel to 26 European countries' },
      { title: 'No Minimum Stay', desc: 'Maintain residency without physical presence requirements' },
      { title: 'Strong Rental Yields', desc: '5-8% annual returns in tourist hotspots' },
      { title: 'Path to Citizenship', desc: 'Eligible for Greek citizenship after 7 years' },
    ],
    
    lifestyle: {
      overview: `Life in Greece moves at a gentler pace. Expect long lunches overlooking the Aegean, evenings at seaside tavernas, and a culture that values family, food, and celebration. Greeks are warm and welcoming to foreigners, and you'll find a growing community of international residents, including Indians.`,
      
      dailyLife: [
        'Morning coffee (frappé culture) at local cafes',
        'Fresh produce from neighborhood markets',
        'Afternoon siestas during summer months',
        'Evening volta (stroll) along waterfronts',
        'Weekend trips to nearby islands'
      ]
    },
    
    food: {
      intro: 'Greek cuisine is a paradise for vegetarians and meat-lovers alike, with fresh ingredients and bold Mediterranean flavors.',
      mustTry: [
        { name: 'Moussaka', desc: 'Layered eggplant, meat, and béchamel - Greece\'s comfort food' },
        { name: 'Fresh Seafood', desc: 'Grilled octopus, calamari, and catch of the day' },
        { name: 'Souvlaki', desc: 'Perfectly grilled meat skewers with tzatziki' },
        { name: 'Dolmades', desc: 'Stuffed grape leaves - vegetarian friendly' },
      ],
      restaurants: [
        { name: 'Varoulko Seaside', location: 'Athens', type: 'Fine Dining', specialty: 'Michelin-starred seafood' },
        { name: 'Selene', location: 'Santorini', type: 'Upscale', specialty: 'Modern Greek cuisine' },
        { name: 'Funky Gourmet', location: 'Athens', type: 'Fine Dining', specialty: 'Molecular gastronomy' },
      ],
      indianFood: 'Indian restaurants available in Athens (Namaste, Indian Haveli). Major cities have Indian grocery stores.'
    },
    
    nightlife: {
      intro: 'Greece comes alive at night, especially during summer. From beach clubs to rooftop bars, there\'s something for every mood.',
      spots: [
        { name: 'Scorpios', location: 'Mykonos', type: 'Beach Club', vibe: 'Sunset parties, international DJs' },
        { name: 'Cavo Paradiso', location: 'Mykonos', type: 'Club', vibe: 'World-famous super club' },
        { name: 'A for Athens', location: 'Athens', type: 'Rooftop Bar', vibe: 'Acropolis views, cocktails' },
        { name: 'Galaxy Bar', location: 'Athens', type: 'Hotel Bar', vibe: 'Sophisticated, panoramic city views' },
        { name: 'Astra', location: 'Santorini', type: 'Club', vibe: 'Open-air clubbing with caldera views' },
      ]
    },
    
    attractions: [
      { name: 'Acropolis', type: 'Historical', desc: 'Ancient citadel and Parthenon - must see' },
      { name: 'Santorini Caldera', type: 'Natural', desc: 'Volcanic cliffs with stunning sunsets' },
      { name: 'Meteora Monasteries', type: 'Spiritual', desc: 'Monasteries atop towering rock pillars' },
      { name: 'Mykonos Beaches', type: 'Leisure', desc: 'Crystal-clear waters and vibrant beach scene' },
      { name: 'Delphi', type: 'Historical', desc: 'Ancient sanctuary of Apollo' },
    ],
    
    practicalTips: [
      'Athens has excellent international schools',
      'Healthcare is high quality and affordable',
      'English is widely spoken in tourist areas',
      'Direct flights from Mumbai and Delhi (seasonal)',
      'Strong 4G/5G coverage across the country',
    ],
    
    cities: ['Athens', 'Santorini', 'Mykonos', 'Thessaloniki', 'Crete'],
  },
  
  'uae': {
    id: 'uae',
    name: 'UAE (Dubai)',
    tagline: 'The Future is Now - Tax-Free Luxury Living',
    heroImage: 'https://images.pexels.com/photos/1470502/pexels-photo-1470502.jpeg',
    region: 'Middle East',
    climate: 'Desert',
    timezone: 'GMT+4',
    flightFromIndia: '3-4 hours',
    bestTime: 'October - April',
    language: 'Arabic, English widely spoken',
    currency: 'AED (Dirham)',
    indianCommunity: 'Largest expat group - 3.5 million Indians',
    
    intro: `Dubai is already home to millions of Indians and offers a familiar yet futuristic lifestyle. With zero income tax, world-class infrastructure, and the new 10-year Golden Visa, UAE has become the preferred choice for Indian HNIs seeking global mobility without straying too far from home.`,
    
    whyInvest: [
      { title: '10-Year Golden Visa', desc: 'Long-term residency with property investment of AED 2M+' },
      { title: 'Zero Income Tax', desc: 'Keep 100% of your earnings' },
      { title: '100% Property Ownership', desc: 'Freehold ownership in designated areas' },
      { title: 'Just 3-4 Hours Away', desc: 'Multiple daily flights from all Indian metros' },
      { title: 'Business Hub', desc: 'Perfect base for Middle East & Africa operations' },
    ],
    
    lifestyle: {
      overview: `Dubai offers Indians a "home away from home" experience. With Indian restaurants, temples, cultural events, and a massive Indian community, you'll never feel far from home while enjoying a tax-free, luxurious lifestyle with world-class amenities.`,
      
      dailyLife: [
        'Morning workouts at luxury fitness centers',
        'Brunch culture at 5-star hotels',
        'Mall visits (air-conditioned comfort)',
        'Beach clubs and pool days',
        'Evening walks along Dubai Marina or JBR'
      ]
    },
    
    food: {
      intro: 'Dubai is a culinary paradise with every cuisine imaginable. Indian food here rivals the best in India itself.',
      mustTry: [
        { name: 'Arabic Mezze', desc: 'Hummus, falafel, shawarma - Middle Eastern staples' },
        { name: 'Indian Street Food', desc: 'Authentic chaat, dosa, biryani everywhere' },
        { name: 'Seafood', desc: 'Fresh catches at traditional fish souks' },
        { name: 'International Fusion', desc: 'Celebrity chef restaurants in abundance' },
      ],
      restaurants: [
        { name: 'Tresind Studio', location: 'DIFC', type: 'Fine Dining', specialty: '2 Michelin stars, Modern Indian' },
        { name: 'Zuma', location: 'DIFC', type: 'Upscale', specialty: 'Japanese izakaya' },
        { name: 'Nobu', location: 'Atlantis', type: 'Celebrity Chef', specialty: 'Japanese-Peruvian' },
      ],
      indianFood: 'Thousands of Indian restaurants from street food to fine dining. All major Indian grocery chains present.'
    },
    
    nightlife: {
      intro: 'Dubai\'s nightlife is legendary - from beach clubs to sky-high lounges, every night is an event.',
      spots: [
        { name: 'White Dubai', location: 'Meydan', type: 'Super Club', vibe: 'International DJs, 10,000 capacity' },
        { name: 'Atmosphere', location: 'Burj Khalifa', type: 'Lounge', vibe: 'World\'s highest bar at 122nd floor' },
        { name: 'Nikki Beach', location: 'Pearl Jumeira', type: 'Beach Club', vibe: 'Day-to-night parties' },
        { name: 'Cavalli Club', location: 'Fairmont', type: 'Club', vibe: 'Opulent Italian designer club' },
        { name: 'Zero Gravity', location: 'Dubai Marina', type: 'Beach Club', vibe: 'Skydive views, pool parties' },
      ]
    },
    
    attractions: [
      { name: 'Burj Khalifa', type: 'Landmark', desc: 'World\'s tallest building - iconic views' },
      { name: 'Palm Jumeirah', type: 'Lifestyle', desc: 'Man-made island with luxury resorts' },
      { name: 'Dubai Mall', type: 'Shopping', desc: 'World\'s largest mall with aquarium' },
      { name: 'Desert Safari', type: 'Adventure', desc: 'Dune bashing, camel rides, BBQ dinner' },
      { name: 'Dubai Marina', type: 'Lifestyle', desc: 'Waterfront living and dining' },
    ],
    
    practicalTips: [
      'Indian curriculum schools (CBSE, ICSE) widely available',
      'World-class healthcare with Indian doctors',
      'Hindi/Urdu widely understood',
      'Indian festivals celebrated publicly',
      'Easy business setup in free zones',
    ],
    
    cities: ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ras Al Khaimah'],
  },
  
  'uk': {
    id: 'uk',
    name: 'United Kingdom',
    tagline: 'The Gateway to Global Finance and Education',
    heroImage: 'https://images.pexels.com/photos/672532/pexels-photo-672532.jpeg',
    region: 'Europe',
    climate: 'Temperate',
    timezone: 'GMT',
    flightFromIndia: '9-10 hours',
    bestTime: 'May - September',
    language: 'English',
    currency: 'GBP (£)',
    indianCommunity: '1.5+ million Indians, well-established community',
    
    intro: `The United Kingdom has been a preferred destination for Indian families for generations. With world-renowned universities, a thriving Indian community, and London's status as a global financial hub, the UK offers unparalleled opportunities for education, business, and lifestyle.`,
    
    whyInvest: [
      { title: 'Stable Property Market', desc: 'London property is a global safe haven asset' },
      { title: 'World-Class Education', desc: 'Oxford, Cambridge, and top boarding schools' },
      { title: 'Global Business Hub', desc: 'London is the world\'s leading financial center' },
      { title: 'Cultural Familiarity', desc: 'Large Indian community with temples, restaurants' },
      { title: 'Strong Rental Market', desc: 'Consistent demand in major cities' },
    ],
    
    lifestyle: {
      overview: `Life in the UK offers a perfect blend of tradition and modernity. From afternoon tea to Premier League matches, from West End shows to country weekends, the British lifestyle is sophisticated yet accessible. The Indian community here is well-integrated and thriving.`,
      
      dailyLife: [
        'Morning coffee at artisan cafes',
        'Pub culture - post-work drinks are tradition',
        'Weekend farmers markets',
        'Theatre and cultural events',
        'Countryside escapes to Cotswolds or Lake District'
      ]
    },
    
    food: {
      intro: 'British cuisine has evolved dramatically. London is now one of the world\'s top food cities, and Indian food is essentially the national cuisine.',
      mustTry: [
        { name: 'Full English Breakfast', desc: 'The ultimate weekend indulgence' },
        { name: 'Sunday Roast', desc: 'Traditional British family meal' },
        { name: 'Fish and Chips', desc: 'Classic British comfort food' },
        { name: 'Chicken Tikka Masala', desc: 'Britain\'s favorite dish - invented here!' },
      ],
      restaurants: [
        { name: 'Gymkhana', location: 'Mayfair', type: 'Fine Dining', specialty: 'Michelin-starred Indian' },
        { name: 'Dishoom', location: 'Multiple', type: 'Casual', specialty: 'Bombay-style café' },
        { name: 'The Ledbury', location: 'Notting Hill', type: 'Fine Dining', specialty: 'Modern European' },
      ],
      indianFood: 'Exceptional Indian food everywhere - from Brick Lane to Michelin-starred restaurants. Large Indian grocery stores in all cities.'
    },
    
    nightlife: {
      intro: 'London\'s nightlife is diverse and legendary, from historic pubs to cutting-edge clubs.',
      spots: [
        { name: 'Fabric', location: 'Farringdon', type: 'Club', vibe: 'Legendary electronic music venue' },
        { name: 'Annabel\'s', location: 'Mayfair', type: 'Members Club', vibe: 'Ultra-exclusive, celebrity haunt' },
        { name: 'Sky Garden', location: 'City', type: 'Rooftop Bar', vibe: '360° London views, free entry' },
        { name: 'The Churchill Bar', location: 'Marylebone', type: 'Cocktail Bar', vibe: 'Sophisticated, award-winning' },
        { name: 'Ministry of Sound', location: 'Elephant & Castle', type: 'Club', vibe: 'Dance music institution' },
      ]
    },
    
    attractions: [
      { name: 'Tower of London', type: 'Historical', desc: 'Crown Jewels and 1000 years of history' },
      { name: 'British Museum', type: 'Cultural', desc: 'World\'s greatest collection - free entry' },
      { name: 'West End', type: 'Entertainment', desc: 'World-class theatre district' },
      { name: 'Cotswolds', type: 'Countryside', desc: 'Quintessential English villages' },
      { name: 'Edinburgh', type: 'City', desc: 'Scotland\'s stunning capital' },
    ],
    
    practicalTips: [
      'Excellent Indian curriculum schools available',
      'NHS provides universal healthcare',
      'Large Indian community festivals (Diwali in Trafalgar Square)',
      'Direct flights from 6+ Indian cities',
      'Strong Indian professional networks',
    ],
    
    cities: ['London', 'Manchester', 'Birmingham', 'Edinburgh', 'Cambridge'],
  },
  
  'portugal': {
    id: 'portugal',
    name: 'Portugal',
    tagline: 'Europe\'s Best-Kept Secret for Quality Living',
    heroImage: 'https://images.pexels.com/photos/2549018/pexels-photo-2549018.jpeg',
    region: 'Europe',
    climate: 'Mediterranean',
    timezone: 'GMT',
    flightFromIndia: '10-12 hours',
    bestTime: 'March - October',
    language: 'Portuguese, English increasingly common',
    currency: 'Euro (€)',
    indianCommunity: 'Small but growing community',
    
    intro: `Portugal has emerged as one of Europe's most desirable destinations, offering exceptional quality of life, affordable luxury, and a welcoming attitude toward immigrants. The Golden Visa program, combined with the Non-Habitual Resident tax regime, makes Portugal particularly attractive for Indian investors and retirees.`,
    
    whyInvest: [
      { title: 'Golden Visa Program', desc: 'EU residency through €500K+ investment' },
      { title: 'NHR Tax Benefits', desc: 'Potential 0% tax on foreign income for 10 years' },
      { title: 'EU Citizenship Path', desc: 'Citizenship after 5 years' },
      { title: 'Safe & Stable', desc: 'One of Europe\'s safest countries' },
      { title: 'Affordable Luxury', desc: '30-40% cheaper than Western Europe' },
    ],
    
    lifestyle: {
      overview: `Portuguese lifestyle emphasizes balance - work to live, not live to work. Expect long lunches, family dinners, beach weekends, and a genuine appreciation for life's simple pleasures. The country is safe, the people are friendly, and the pace is relaxed.`,
      
      dailyLife: [
        'Pastel de nata with morning coffee',
        'Long lunches are the norm',
        'Beach culture in summer',
        'Fado music evenings',
        'Weekend trips to wine regions'
      ]
    },
    
    food: {
      intro: 'Portuguese cuisine is hearty, fresh, and centered around seafood. The country is also a wine lover\'s paradise.',
      mustTry: [
        { name: 'Bacalhau', desc: 'Salt cod prepared 1000 different ways' },
        { name: 'Pastel de Nata', desc: 'Iconic custard tarts - addictive!' },
        { name: 'Fresh Seafood', desc: 'Grilled sardines, prawns, octopus' },
        { name: 'Francesinha', desc: 'Porto\'s famous meat sandwich' },
      ],
      restaurants: [
        { name: 'Belcanto', location: 'Lisbon', type: 'Fine Dining', specialty: '2 Michelin stars, modern Portuguese' },
        { name: 'Time Out Market', location: 'Lisbon', type: 'Food Hall', specialty: 'Best of Lisbon under one roof' },
        { name: 'The Yeatman', location: 'Porto', type: 'Fine Dining', specialty: 'Wine-pairing experiences' },
      ],
      indianFood: 'Limited but growing. Lisbon has several Indian restaurants. Goan influence means some familiar flavors.'
    },
    
    nightlife: {
      intro: 'Portugal offers diverse nightlife from historic Fado houses to modern clubs, especially in Lisbon.',
      spots: [
        { name: 'Lux Frágil', location: 'Lisbon', type: 'Club', vibe: 'Europe\'s best club, rooftop views' },
        { name: 'Pensão Amor', location: 'Lisbon', type: 'Bar', vibe: 'Former brothel turned trendy bar' },
        { name: 'Bairro Alto', location: 'Lisbon', type: 'District', vibe: 'Street party every night' },
        { name: 'Tasca do Chico', location: 'Lisbon', type: 'Fado House', vibe: 'Authentic Fado experience' },
        { name: 'Wine Quay Bar', location: 'Porto', type: 'Wine Bar', vibe: 'Douro views, port wine' },
      ]
    },
    
    attractions: [
      { name: 'Sintra Palaces', type: 'Historical', desc: 'Fairytale castles in misty mountains' },
      { name: 'Douro Valley', type: 'Wine Region', desc: 'UNESCO World Heritage wine region' },
      { name: 'Algarve Coast', type: 'Beach', desc: 'Europe\'s best beaches and golf' },
      { name: 'Porto Ribeira', type: 'Cultural', desc: 'Historic riverfront, port wine cellars' },
      { name: 'Belém', type: 'Historical', desc: 'Maritime monuments, custard tarts' },
    ],
    
    practicalTips: [
      'English widely spoken in Lisbon and tourist areas',
      'Excellent and affordable private healthcare',
      'International schools in Lisbon and Algarve',
      'Very safe for families',
      'Good digital nomad infrastructure',
    ],
    
    cities: ['Lisbon', 'Porto', 'Algarve', 'Cascais', 'Sintra'],
  },
  
  'usa': {
    id: 'usa',
    name: 'United States',
    tagline: 'The Land of Opportunity Awaits',
    heroImage: 'https://images.pexels.com/photos/290386/pexels-photo-290386.jpeg',
    region: 'Americas',
    climate: 'Varied',
    timezone: 'GMT-5 to GMT-10',
    flightFromIndia: '15-18 hours',
    bestTime: 'Year-round (varies by region)',
    language: 'English',
    currency: 'USD ($)',
    indianCommunity: '4.5+ million Indians, highly successful community',
    
    intro: `The United States remains the ultimate destination for Indians seeking global opportunities. With the world's largest economy, top universities, and an established Indian community that has risen to the highest levels of business and politics, America continues to be the land where dreams come true.`,
    
    whyInvest: [
      { title: 'EB-5 Green Card', desc: 'Permanent residency through $800K+ investment' },
      { title: 'World\'s Largest Market', desc: 'Unmatched business opportunities' },
      { title: 'Top Education', desc: 'Harvard, Stanford, MIT and more' },
      { title: 'Established Indian Network', desc: 'CEOs of Google, Microsoft, Adobe are Indian' },
      { title: 'Property Appreciation', desc: 'Strong long-term value growth' },
    ],
    
    lifestyle: {
      overview: `America offers unparalleled diversity - from the fast pace of New York to the laid-back vibes of California. Indians have thrived here, building communities, temples, and businesses. Your children will have access to the world's best opportunities.`,
      
      dailyLife: [
        'Car-centric suburban lifestyle (in most cities)',
        'Weekend brunches and sports events',
        'BBQ culture and outdoor activities',
        'Road trips and national park visits',
        'Community events at local temples'
      ]
    },
    
    food: {
      intro: 'America is a melting pot of cuisines. You\'ll find everything from authentic regional Indian to innovative fusion.',
      mustTry: [
        { name: 'BBQ', desc: 'Texas brisket, Carolina pulled pork - American art form' },
        { name: 'NYC Pizza', desc: 'Iconic New York-style thin crust' },
        { name: 'Californian Cuisine', desc: 'Farm-to-table, health-conscious innovation' },
        { name: 'Cajun/Creole', desc: 'New Orleans spicy, flavorful cooking' },
      ],
      restaurants: [
        { name: 'Junoon', location: 'New York', type: 'Fine Dining', specialty: 'Michelin-starred Indian' },
        { name: 'Eleven Madison Park', location: 'New York', type: 'Fine Dining', specialty: 'World\'s best restaurant' },
        { name: 'French Laundry', location: 'Napa Valley', type: 'Fine Dining', specialty: 'Thomas Keller\'s masterpiece' },
      ],
      indianFood: 'Excellent Indian food in every major city. Edison NJ, Sunnyvale CA, and Houston have "Little India" areas.'
    },
    
    nightlife: {
      intro: 'American cities never sleep - from Vegas clubs to Miami beaches to Brooklyn warehouses.',
      spots: [
        { name: 'Marquee', location: 'Las Vegas', type: 'Super Club', vibe: 'Vegas extravagance, top DJs' },
        { name: 'LIV', location: 'Miami', type: 'Club', vibe: 'Celebrity hotspot, bottle service' },
        { name: 'The Standard Rooftop', location: 'New York', type: 'Rooftop Bar', vibe: 'Manhattan skyline views' },
        { name: 'Blind Barber', location: 'Los Angeles', type: 'Speakeasy', vibe: 'Hidden cocktail bar' },
        { name: '1 OAK', location: 'New York', type: 'Club', vibe: 'A-list celebrities, exclusive' },
      ]
    },
    
    attractions: [
      { name: 'Grand Canyon', type: 'Natural', desc: 'One of the world\'s natural wonders' },
      { name: 'New York City', type: 'City', desc: 'The city that never sleeps' },
      { name: 'Silicon Valley', type: 'Business', desc: 'Global tech innovation hub' },
      { name: 'Las Vegas', type: 'Entertainment', desc: 'World entertainment capital' },
      { name: 'Hawaii', type: 'Beach', desc: 'Tropical paradise, part of USA' },
    ],
    
    practicalTips: [
      'Many Indian-curriculum schools in major metros',
      'Health insurance essential - plan carefully',
      'Strong Indian professional associations',
      'Multiple direct flights from India',
      'H-1B visa experience helps with EB-5 transition',
    ],
    
    cities: ['New York', 'San Francisco', 'Los Angeles', 'Miami', 'Houston'],
  },
  
  'singapore': {
    id: 'singapore',
    name: 'Singapore',
    tagline: 'Asia\'s Premier Global City',
    heroImage: 'https://images.pexels.com/photos/777059/pexels-photo-777059.jpeg',
    region: 'Asia',
    climate: 'Tropical',
    timezone: 'GMT+8',
    flightFromIndia: '4-5 hours',
    bestTime: 'Year-round',
    language: 'English, Mandarin, Tamil, Malay',
    currency: 'SGD ($)',
    indianCommunity: '350,000+ Indians, Tamil is official language',
    
    intro: `Singapore is Asia's Switzerland - a clean, efficient, safe, and prosperous city-state where Indians have flourished for generations. With Tamil as an official language, Little India as a vibrant cultural hub, and world-class infrastructure, Singapore offers Indians a familiar yet elevated lifestyle.`,
    
    whyInvest: [
      { title: 'Global Business Hub', desc: 'Asia\'s most business-friendly environment' },
      { title: 'Safety & Stability', desc: 'One of world\'s safest countries' },
      { title: 'Tax Efficiency', desc: 'Low taxes, no capital gains tax' },
      { title: 'Education Excellence', desc: 'Top-ranked Asian universities' },
      { title: 'Indian Familiar', desc: 'Tamil official, large Indian community' },
    ],
    
    lifestyle: {
      overview: `Singapore offers an efficient, orderly lifestyle with all the comforts of home. Little India provides authentic Indian culture, while the rest of the city offers world-class shopping, dining, and entertainment. Everything works perfectly here.`,
      
      dailyLife: [
        'Morning kopi at hawker centers',
        'Air-conditioned malls for daily errands',
        'Evening jogs at Gardens by the Bay',
        'Weekend visits to Sentosa or Little India',
        'World-class food at every price point'
      ]
    },
    
    food: {
      intro: 'Singapore is a food paradise. Hawker centers offer incredible meals for $3, while Marina Bay has Michelin-starred restaurants.',
      mustTry: [
        { name: 'Chicken Rice', desc: 'National dish - deceptively simple, incredibly good' },
        { name: 'Laksa', desc: 'Spicy coconut curry noodle soup' },
        { name: 'Chili Crab', desc: 'Iconic Singaporean seafood dish' },
        { name: 'Roti Prata', desc: 'Indian flatbread, local style - 24/7 availability' },
      ],
      restaurants: [
        { name: 'Odette', location: 'Marina Bay', type: 'Fine Dining', specialty: '3 Michelin stars, French' },
        { name: 'Burnt Ends', location: 'Chinatown', type: 'Upscale', specialty: 'Australian BBQ, 1 Michelin star' },
        { name: 'Song of India', location: 'Newton', type: 'Fine Dining', specialty: 'Michelin-starred Indian' },
      ],
      indianFood: 'Little India has everything - from banana leaf meals to biryani. Tekka Centre and Mustafa are legends.'
    },
    
    nightlife: {
      intro: 'Singapore\'s nightlife is sophisticated and diverse, from Clarke Quay bars to rooftop lounges.',
      spots: [
        { name: 'Ce La Vi', location: 'Marina Bay Sands', type: 'Rooftop Club', vibe: 'Iconic infinity pool views' },
        { name: 'Zouk', location: 'Clarke Quay', type: 'Super Club', vibe: 'Asia\'s best club' },
        { name: 'Atlas', location: 'Beach Road', type: 'Bar', vibe: 'World\'s best bar, art deco grandeur' },
        { name: '1-Altitude', location: 'CBD', type: 'Rooftop', vibe: 'World\'s highest alfresco bar' },
        { name: 'Tippling Club', location: 'Tanjong Pagar', type: 'Cocktail Bar', vibe: 'Innovative cocktails' },
      ]
    },
    
    attractions: [
      { name: 'Marina Bay Sands', type: 'Landmark', desc: 'Iconic hotel with infinity pool' },
      { name: 'Gardens by the Bay', type: 'Nature', desc: 'Futuristic gardens and Supertrees' },
      { name: 'Sentosa Island', type: 'Resort', desc: 'Beaches, Universal Studios, casinos' },
      { name: 'Little India', type: 'Cultural', desc: 'Authentic Indian neighborhood' },
      { name: 'Orchard Road', type: 'Shopping', desc: 'Premium shopping boulevard' },
    ],
    
    practicalTips: [
      'Indian curriculum schools readily available',
      'World-class healthcare system',
      'Tamil is an official language',
      'Multiple daily flights from India',
      'Low crime, strict but fair laws',
    ],
    
    cities: ['Singapore City', 'Sentosa', 'Jurong', 'Marina Bay'],
  },
  
  'canada': {
    id: 'canada',
    name: 'Canada',
    tagline: 'Quality of Life Meets Global Opportunity',
    heroImage: 'https://images.pexels.com/photos/1519088/pexels-photo-1519088.jpeg',
    region: 'Americas',
    climate: 'Varied (mostly cold winters)',
    timezone: 'GMT-3.5 to GMT-8',
    flightFromIndia: '14-16 hours',
    bestTime: 'May - September',
    language: 'English, French',
    currency: 'CAD ($)',
    indianCommunity: '1.4+ million Indians, rapidly growing',
    
    intro: `Canada has become the top destination for Indians seeking permanent residency abroad. With its welcoming immigration policies, multicultural society, free healthcare, and excellent education, Canada offers Indians a high quality of life with a clear path to citizenship.`,
    
    whyInvest: [
      { title: 'Welcoming Immigration', desc: 'Canada actively welcomes skilled immigrants' },
      { title: 'Free Healthcare', desc: 'Universal healthcare for all residents' },
      { title: 'Multicultural Society', desc: 'Official policy of multiculturalism' },
      { title: 'Top Education', desc: 'World-class universities and schools' },
      { title: 'Citizenship Path', desc: 'PR to citizenship in 3-5 years' },
    ],
    
    lifestyle: {
      overview: `Canadian life balances work and play beautifully. Summers are for cottages, BBQs, and outdoor festivals. Winters are for skiing, hockey, and cozy indoor gatherings. The Indian community is thriving, with temples, events, and cultural celebrations everywhere.`,
      
      dailyLife: [
        'Tim Hortons coffee runs',
        'Winter sports and summer cottages',
        'Multicultural neighborhood festivals',
        'Hockey night in Canada',
        'Weekend trips to nearby nature'
      ]
    },
    
    food: {
      intro: 'Canada\'s food scene reflects its diversity. From poutine to butter chicken, from sushi to shawarma - it\'s all here.',
      mustTry: [
        { name: 'Poutine', desc: 'Fries, cheese curds, gravy - Canadian comfort' },
        { name: 'Montreal Bagels', desc: 'Sweeter, denser than New York style' },
        { name: 'Butter Tarts', desc: 'Classic Canadian pastry' },
        { name: 'Pacific Salmon', desc: 'BC\'s incredible fresh seafood' },
      ],
      restaurants: [
        { name: 'Alo', location: 'Toronto', type: 'Fine Dining', specialty: 'Canada\'s best restaurant' },
        { name: 'Pujol', location: 'Vancouver', type: 'Fine Dining', specialty: 'Modern Mexican' },
        { name: 'Joe Beef', location: 'Montreal', type: 'Upscale', specialty: 'Iconic Montreal institution' },
      ],
      indianFood: 'Brampton is "Little Punjab" - authentic Punjabi food everywhere. Surrey BC has excellent Indian restaurants too.'
    },
    
    nightlife: {
      intro: 'Canadian cities have vibrant nightlife, from Toronto\'s clubs to Montreal\'s 3am last calls.',
      spots: [
        { name: 'Rebel', location: 'Toronto', type: 'Super Club', vibe: 'Canada\'s largest nightclub' },
        { name: 'Marquee', location: 'Vancouver', type: 'Club', vibe: 'EDM and hip-hop' },
        { name: 'New City Gas', location: 'Montreal', type: 'Club', vibe: 'Industrial chic, late nights' },
        { name: 'Bar Raval', location: 'Toronto', type: 'Bar', vibe: 'Stunning Gaudi-inspired space' },
        { name: 'Prohibition', location: 'Vancouver', type: 'Speakeasy', vibe: 'Craft cocktails' },
      ]
    },
    
    attractions: [
      { name: 'Niagara Falls', type: 'Natural', desc: 'World-famous waterfall' },
      { name: 'Banff National Park', type: 'Nature', desc: 'Rocky Mountain paradise' },
      { name: 'CN Tower', type: 'Landmark', desc: 'Toronto\'s iconic tower' },
      { name: 'Old Montreal', type: 'Historical', desc: 'European charm in North America' },
      { name: 'Vancouver Seawall', type: 'Lifestyle', desc: 'World\'s longest waterfront path' },
    ],
    
    practicalTips: [
      'Indian grocery stores in all major cities',
      'Free healthcare (after qualifying period)',
      'Excellent public schools',
      'Direct flights from Delhi, Mumbai, Vancouver',
      'Large Sikh and Hindu communities',
    ],
    
    cities: ['Toronto', 'Vancouver', 'Montreal', 'Calgary', 'Ottawa'],
  },
  
  'egypt': {
    id: 'egypt',
    name: 'Egypt',
    tagline: 'Ancient Wonders Meet Modern Investment',
    heroImage: 'https://images.pexels.com/photos/3214958/pexels-photo-3214958.jpeg',
    region: 'Africa',
    climate: 'Desert',
    timezone: 'GMT+2',
    flightFromIndia: '5-6 hours',
    bestTime: 'October - April',
    language: 'Arabic, English in tourist areas',
    currency: 'EGP (£)',
    indianCommunity: 'Small but present community',
    
    intro: `Egypt offers Indian investors an emerging market opportunity combined with legendary history and strategic location. The Red Sea resorts like El Gouna provide luxury living at a fraction of European prices, while Cairo offers metropolitan lifestyle with African and Middle Eastern influences.`,
    
    whyInvest: [
      { title: 'Affordable Entry', desc: 'Residency through $300K+ property investment' },
      { title: 'Strategic Location', desc: 'Gateway between Africa, Middle East, and Europe' },
      { title: 'Growing Economy', desc: 'Major economic reforms driving growth' },
      { title: 'Tourism Potential', desc: 'Strong rental yields in resort areas' },
      { title: 'Value Appreciation', desc: 'Emerging market growth potential' },
    ],
    
    lifestyle: {
      overview: `Egypt offers a unique blend of ancient history and modern resort living. El Gouna on the Red Sea provides a European-style resort experience, while Cairo offers bustling metropolitan life. The cost of living is very affordable compared to other destinations.`,
      
      dailyLife: [
        'Morning coffee at ahwas (traditional cafes)',
        'Fresh fruit juice stands everywhere',
        'Beach and water sports at Red Sea resorts',
        'Evening walks along the Nile',
        'Weekend trips to historical sites'
      ]
    },
    
    food: {
      intro: 'Egyptian cuisine is flavorful, vegetarian-friendly, and affordable. Fresh juices and street food are exceptional.',
      mustTry: [
        { name: 'Koshari', desc: 'National dish - rice, lentils, pasta, tomato sauce' },
        { name: 'Ful Medames', desc: 'Fava bean breakfast staple' },
        { name: 'Shawarma', desc: 'Egyptian-style wrapped meat' },
        { name: 'Fresh Seafood', desc: 'Red Sea catches - grilled simply' },
      ],
      restaurants: [
        { name: 'Abou El Sid', location: 'Cairo', type: 'Traditional', specialty: 'Authentic Egyptian cuisine' },
        { name: 'The Smokery', location: 'Cairo', type: 'Upscale', specialty: 'Modern Egyptian seafood' },
        { name: 'Pier 88', location: 'El Gouna', type: 'Beach', specialty: 'Mediterranean seafood' },
      ],
      indianFood: 'Limited - mainly in Cairo hotels. Consider this a place to explore Egyptian cuisine.'
    },
    
    nightlife: {
      intro: 'Cairo has a vibrant nightlife scene, while El Gouna offers resort-style entertainment.',
      spots: [
        { name: 'Cairo Jazz Club', location: 'Cairo', type: 'Live Music', vibe: 'Best live music venue' },
        { name: 'Tamarai', location: 'Cairo', type: 'Club', vibe: 'Nile-side super club' },
        { name: 'Bartolotta', location: 'El Gouna', type: 'Beach Bar', vibe: 'Marina views, cocktails' },
        { name: 'Warehouse', location: 'El Gouna', type: 'Club', vibe: 'Resort nightclub' },
        { name: 'Rooftop at Four Seasons', location: 'Cairo', type: 'Lounge', vibe: 'Pyramid views' },
      ]
    },
    
    attractions: [
      { name: 'Pyramids of Giza', type: 'Historical', desc: 'Last standing wonder of ancient world' },
      { name: 'Luxor Temples', type: 'Historical', desc: 'World\'s greatest open-air museum' },
      { name: 'Red Sea Diving', type: 'Adventure', desc: 'World-class coral reefs and wrecks' },
      { name: 'Nile Cruise', type: 'Experience', desc: 'Sail through 5000 years of history' },
      { name: 'Khan El Khalili', type: 'Cultural', desc: 'Historic bazaar, 600 years old' },
    ],
    
    practicalTips: [
      'English spoken in tourist areas and resorts',
      'Private healthcare recommended',
      'Very affordable cost of living',
      'Direct flights from Mumbai and Delhi',
      'Excellent value for luxury properties',
    ],
    
    cities: ['Cairo', 'El Gouna', 'Alexandria', 'Hurghada', 'Sharm El Sheikh'],
  },
  'alibaug': {
    id: 'alibaug',
    name: 'Alibaug',
    tagline: "Mumbai's Riviera — Where the City Meets the Sea",
    heroImage: 'https://images.unsplash.com/photo-1757439402277-1a88c7abc89d?w=1600',
    region: 'India',
    climate: 'Tropical Coastal',
    timezone: 'GMT+5:30',
    flightFromIndia: 'N/A — 90 min drive from Mumbai via Atal Setu',
    bestTime: 'October - March',
    language: 'Marathi, Hindi, English',
    currency: 'Indian Rupee (INR)',
    indianCommunity: "India's premier weekend villa destination for Mumbai's elite",
    intro: "Alibaug has transformed from a quiet Konkan town to India's most coveted villa destination. With the Atal Setu (Mumbai Trans Harbour Link) cutting travel time to under 90 minutes, Alibaug is now Mumbai's answer to the Hamptons. Think lush green landscapes, pristine beaches, and a growing ecosystem of luxury villas, farm-to-table restaurants, and boutique experiences — all at a fraction of international prices.",
    whyInvest: [
      { title: 'Atal Setu Connectivity', desc: 'Sea link has slashed Mumbai-Alibaug travel to 90 minutes, driving massive appreciation' },
      { title: 'Price Appreciation', desc: 'Land prices have doubled in 3 years with further runway ahead' },
      { title: 'Weekend Rental Demand', desc: 'Strong 15-20% yields on premium villas during peak season' },
      { title: 'NAINA Smart City', desc: 'Part of Navi Mumbai Airport Influence Notified Area development plan' },
      { title: 'Limited Supply', desc: 'Coastal regulation limits new development, protecting existing property values' },
    ],
    lifestyle: {
      overview: "Life in Alibaug is the ultimate balance — the serenity of a coastal hamlet with weekend access to Mumbai's energy. Mornings start with walks on Kashid or Nagaon beach, afternoons at a farm-to-fork cafe, and evenings watching sunsets from your villa's infinity pool. A growing community of artists, entrepreneurs, and industrialists is making Alibaug India's most interesting micro-destination.",
      dailyLife: ['Morning walks on Kihim Beach', 'Cycling through cashew and coconut plantations', 'Farm-to-table dining at boutique restaurants', 'Water sports — kayaking, parasailing, jet skiing', 'Weekend entertaining at your private villa pool']
    },
    food: {
      intro: 'Alibaug blends traditional Konkan seafood with a new wave of gourmet dining driven by Mumbai chefs who have set up shop here.',
      mustTry: [
        { name: 'Bombil Fry', desc: 'Crispy fried Bombay duck — a Konkan coastal staple' },
        { name: 'Sol Kadhi', desc: 'Refreshing kokum and coconut milk drink, perfect post-meal' },
        { name: 'Malvani Fish Curry', desc: 'Spicy coconut-based curry with fresh catch of the day' },
        { name: 'Modak', desc: 'Sweet coconut dumplings — a Maharashtrian delicacy' },
      ],
      restaurants: [
        { name: 'The Outrigger', location: 'Mandwa', type: 'Fine Dining', specialty: 'Coastal Indian with a modern twist' },
        { name: 'Bohemyan Blue', location: 'Alibaug', type: 'Cafe', specialty: 'Art-centric cafe with global menu' },
        { name: 'Sula Vineyards Tasting Room', location: 'Near Alibaug', type: 'Wine Bar', specialty: 'Indian wines and cheese platters' },
      ],
      indianFood: 'Abundant local Maharashtrian and Konkan cuisine. Many Mumbai-quality restaurants now operate in Alibaug.'
    },
    nightlife: {
      intro: "Alibaug's nightlife is intimate rather than loud — think private villa parties, bonfire evenings, and boutique bar experiences.",
      spots: [
        { name: 'Radisson Blu Resort Pool Bar', location: 'Alibaug', type: 'Resort Bar', vibe: 'Relaxed poolside cocktails' },
        { name: 'Full Moon Bonfire', location: 'Kashid Beach', type: 'Beach', vibe: 'Private bonfire gatherings under the stars' },
        { name: 'Villa Privada', location: 'Zirad', type: 'Private', vibe: 'Invite-only villa parties by Mumbai socialites' },
      ]
    },
    attractions: [
      { name: 'Alibaug Fort (Kolaba Fort)', type: 'Historical', desc: 'Sea fort accessible at low tide — iconic Maratha history' },
      { name: 'Kashid Beach', type: 'Beach', desc: 'White sand beach, one of the cleanest near Mumbai' },
      { name: 'Phansad Wildlife Sanctuary', type: 'Nature', desc: 'Diverse flora and fauna, trekking trails' },
      { name: 'Korlai Fort & Lighthouse', type: 'Historical', desc: 'Portuguese-era fort with panoramic sea views' },
    ],
    investorTips: [
      'Buy in Kihim, Zirad, or Mandwa for best appreciation',
      'Check NA (Non-Agricultural) land conversion status before purchase',
      'Atal Setu has fundamentally changed the market — early movers benefit most',
      'RERA-registered projects offer better legal protection',
      'Weekend rental management services are now available from multiple operators',
    ],
    cities: ['Kihim', 'Zirad', 'Mandwa', 'Nagaon', 'Kashid'],
  },
  'mumbai-3.0': {
    id: 'mumbai-3.0',
    name: 'Mumbai 3.0',
    tagline: 'The Panvel-Karjat Growth Corridor — India\'s Smartest Investment Belt',
    heroImage: 'https://images.unsplash.com/photo-1622015663319-e97e697503ee?w=1600',
    region: 'India',
    climate: 'Tropical',
    timezone: 'GMT+5:30',
    flightFromIndia: 'N/A — Navi Mumbai International Airport opening soon',
    bestTime: 'October - March',
    language: 'Marathi, Hindi, English',
    currency: 'Indian Rupee (INR)',
    indianCommunity: 'Emerging luxury villa corridor for Mumbai professionals',
    intro: "Mumbai 3.0 refers to the transformative Panvel-to-Karjat corridor — a region set to become the next epicenter of Mumbai's growth. Anchored by the upcoming Navi Mumbai International Airport (NMIA), the Mumbai-Pune Expressway, and the NAINA smart city project, this belt is attracting forward-thinking investors looking for luxury villa living with world-class infrastructure at pre-breakout prices.",
    whyInvest: [
      { title: 'NMIA Airport', desc: 'Navi Mumbai International Airport will transform this corridor into a premium address' },
      { title: 'NAINA Smart City', desc: 'Government-backed smart city development with planned infrastructure' },
      { title: 'Price Advantage', desc: 'Villas at 30-40% discount compared to equivalent Alibaug or Lonavala properties' },
      { title: 'Infrastructure Boom', desc: 'Metro Line 14, Virar-Alibaug Multimodal Corridor, and expressway upgrades' },
      { title: 'Early Mover Advantage', desc: 'Pre-airport opening prices with 3-5x appreciation potential over 5-7 years' },
    ],
    lifestyle: {
      overview: "The Panvel-Karjat corridor offers the best of both worlds — proximity to Mumbai's economic engine while being surrounded by the lush Western Ghats. Imagine waking up to mountain views, spending mornings in your private garden, and being at the airport or BKC within an hour. This is where Mumbai's next chapter is being written.",
      dailyLife: ['Morning treks in the Sahyadri foothills', 'Fresh local produce from weekly haats', 'Weekday commute to Mumbai via expressway or upcoming metro', 'Adventure activities — rappelling, river rafting on Kundalika', 'Community villa living with modern club amenities']
    },
    food: {
      intro: 'The region offers authentic Maharashtrian fare along with a growing number of quality restaurants catering to the new luxury villa communities.',
      mustTry: [
        { name: 'Misal Pav', desc: 'Spicy sprouted lentil curry — Maharashtrian street food royalty' },
        { name: 'Vada Pav', desc: 'The iconic Mumbai snack, perfected at highway dhaba stops' },
        { name: 'Puran Poli', desc: 'Sweet flatbread with lentil filling — festive comfort food' },
        { name: 'Bhakri with Thecha', desc: 'Jowar flatbread with spicy green chili chutney' },
      ],
      restaurants: [
        { name: 'Kinara Village Dhaba', location: 'Old Pune Highway', type: 'Highway', specialty: 'Rustic Maharashtrian in an open-air setting' },
        { name: 'Della Resorts', location: 'Near Karjat', type: 'Resort Dining', specialty: 'Multi-cuisine with adventure park' },
      ],
      indianFood: 'Fully Indian dining ecosystem. Mumbai restaurants expanding to this corridor.'
    },
    nightlife: {
      intro: 'Nightlife in the corridor is villa-community-centered — clubhouse gatherings, private screenings, and resort experiences.',
      spots: [
        { name: 'Della Adventure Resort', location: 'Kunegaon', type: 'Resort', vibe: 'Adventure-meets-luxury entertainment' },
        { name: 'Villa Community Clubhouses', location: 'Various', type: 'Private', vibe: 'Resident social gatherings, movie nights' },
      ]
    },
    attractions: [
      { name: 'Karnala Bird Sanctuary', type: 'Nature', desc: 'Birdwatching paradise with 222+ species' },
      { name: 'Kundalika River Rafting', type: 'Adventure', desc: 'White-water rafting through scenic gorges' },
      { name: 'Bhimashankar Trek', type: 'Nature', desc: 'One of the 12 Jyotirlingas amidst dense forest' },
      { name: 'Imagicaa Theme Park', type: 'Entertainment', desc: 'International-standard theme park nearby' },
    ],
    investorTips: [
      'Focus on NMIA-adjacent areas (Pushpak Nagar, Panvel) for maximum appreciation',
      'NAINA zone properties have higher infrastructure surcharge but better long-term returns',
      'Look for RERA-registered villa projects with clear title',
      'Airport timeline is the key catalyst — invest before commercial operations begin',
      'Consider hillside plots near Karjat for nature-centric luxury builds',
    ],
    cities: ['Panvel', 'Karjat', 'Khopoli', 'Khalapur', 'Neral'],
  },
  'lonavala': {
    id: 'lonavala',
    name: 'Lonavala',
    tagline: 'The Sahyadri Hillstation — Where Mumbai & Pune Retreat',
    heroImage: 'https://images.unsplash.com/photo-1695124568213-f4071fbf516f?w=1600',
    region: 'India',
    climate: 'Tropical Highland',
    timezone: 'GMT+5:30',
    flightFromIndia: 'N/A — 90 min from Mumbai, 60 min from Pune',
    bestTime: 'July - February (Monsoon is spectacular)',
    language: 'Marathi, Hindi, English',
    currency: 'Indian Rupee (INR)',
    indianCommunity: "India's original hillstation villa destination for Mumbai-Pune HNIs",
    intro: "Lonavala-Khandala has been the go-to hillstation for Mumbai and Pune residents for decades. At 2,000 feet elevation in the Western Ghats, it offers cool weather year-round, breathtaking monsoon views, and an established luxury villa ecosystem. The Mumbai-Pune Expressway puts both cities within 90 and 60 minutes respectively, making it ideal for both weekend getaways and semi-permanent living.",
    whyInvest: [
      { title: 'Established Market', desc: 'Proven track record of steady 8-12% annual appreciation' },
      { title: 'Year-Round Demand', desc: 'Strong rental yields in both monsoon and winter seasons' },
      { title: 'Dual City Access', desc: '90 min to Mumbai, 60 min to Pune via expressway' },
      { title: 'Hill Premium', desc: 'Elevation and climate command premium pricing that holds value' },
      { title: 'Limited Buildable Land', desc: 'Western Ghats conservation limits supply, protecting prices' },
    ],
    lifestyle: {
      overview: "Lonavala life is defined by the rhythm of the hills — misty mornings, chai on the verandah overlooking valleys, monsoon waterfalls within walking distance, and starlit nights with zero light pollution. An increasingly sophisticated food and retail scene means you don't sacrifice urban comforts.",
      dailyLife: ['Morning walks with valley views and birdsong', 'Trekking to Tiger Point or Rajmachi Fort', 'Afternoon reading by the fireplace', 'Exploring local chikki shops and artisan markets', 'Evening barbecues on the deck with mountain panoramas']
    },
    food: {
      intro: "Lonavala's food scene has evolved from highway pitstops to genuine gourmet destinations, with several Mumbai-Pune chefs establishing outposts here.",
      mustTry: [
        { name: 'Lonavala Chikki', desc: 'Iconic jaggery and nut brittle — the original Lonavala souvenir' },
        { name: 'Vada Pav at Kinara', desc: 'Legendary highway vada pav that draws crowds for decades' },
        { name: 'Corn on the cob', desc: 'Roasted bhutta with lime and masala at every viewpoint' },
        { name: 'Fudge', desc: 'Cooper\'s and Maganlal\'s fudge — a Lonavala institution' },
      ],
      restaurants: [
        { name: 'The Verandah', location: 'Lonavala', type: 'Fine Dining', specialty: 'Valley-view dining with modern Indian cuisine' },
        { name: 'Della Resorts Restaurant', location: 'Kunegaon', type: 'Resort', specialty: 'Multi-cuisine with valley panoramas' },
        { name: 'Rama Krishna', location: 'Old Mumbai-Pune Highway', type: 'Casual', specialty: 'South Indian classics in hill setting' },
      ],
      indianFood: 'Fully Indian food ecosystem. Wide variety from street food to fine dining.'
    },
    nightlife: {
      intro: "Lonavala's evenings are about intimate gatherings, bonfire nights, and resort experiences rather than clubs.",
      spots: [
        { name: 'Rhythm Lounge', location: 'Lonavala', type: 'Lounge Bar', vibe: 'Live music weekends with valley views' },
        { name: 'Villa Bonfire Nights', location: 'Various', type: 'Private', vibe: 'Star-gazing and bonfire gatherings at private villas' },
        { name: 'The Dukes Retreat', location: 'Khandala', type: 'Resort Bar', vibe: 'Heritage resort cocktails with Duke\'s Nose view' },
      ]
    },
    attractions: [
      { name: 'Tiger Point', type: 'Viewpoint', desc: 'Dramatic cliff viewpoint overlooking the valley' },
      { name: 'Bhushi Dam', type: 'Nature', desc: 'Monsoon waterfall experience — iconic Lonavala' },
      { name: 'Rajmachi Fort', type: 'Trek', desc: 'Historic Maratha fort with stunning monsoon trekking' },
      { name: 'Karla & Bhaja Caves', type: 'Historical', desc: '2nd century BCE Buddhist rock-cut caves' },
    ],
    investorTips: [
      'Premium is on ridge-facing villas with unobstructed valley views',
      'Avoid flood-prone lower areas — hillside and ridge properties hold value better',
      'Check for CRZ compliance if near water bodies',
      'INR 3-8 Cr range offers the best entry points for luxury villas',
      'Monsoon season bookings can cover 40% of annual rental income alone',
    ],
    cities: ['Lonavala', 'Khandala', 'Tungarli', 'Aamby Valley', 'Pavana'],
  },
  'goa': {
    id: 'goa',
    name: 'Goa',
    tagline: 'Coastal Luxury Estates — Where Every Day Feels Like Holiday',
    heroImage: 'https://images.pexels.com/photos/35808145/pexels-photo-35808145.jpeg?w=1600',
    region: 'India',
    climate: 'Tropical Coastal',
    timezone: 'GMT+5:30',
    flightFromIndia: '1.5 hours from Mumbai, 2 hours from Delhi (Manohar International Airport)',
    bestTime: 'October - March',
    language: 'Konkani, English, Hindi',
    currency: 'Indian Rupee (INR)',
    indianCommunity: "India's premier luxury lifestyle destination with a global expat community",
    intro: "Goa has transcended its beach-party reputation to become India's most sophisticated luxury real estate market. The opening of the Manohar International Airport, combined with a thriving community of entrepreneurs, artists, and global remote workers, has created a unique ecosystem. North Goa's Assagao, Siolim, and Anjuna belt is now India's equivalent of Bali's Canggu — premium villas with global design sensibilities, surrounded by paddy fields and Portuguese-era charm.",
    whyInvest: [
      { title: 'New Airport Effect', desc: 'Manohar International Airport (Mopa) has opened North Goa to direct international flights' },
      { title: 'Rental Gold', desc: 'Premium villas in Assagao-Siolim command INR 50,000-2,00,000/night in peak season' },
      { title: 'Global Community', desc: 'Growing ecosystem of HNIs, digital nomads, and international buyers' },
      { title: 'Cultural Capital', desc: 'Art galleries, design studios, farm-to-table restaurants — India\'s most cultured coastline' },
      { title: 'Steady Appreciation', desc: 'North Goa villa land has appreciated 15-20% annually over the past 5 years' },
    ],
    lifestyle: {
      overview: "Life in Goa is the ultimate synthesis of Indian warmth and global sophistication. Mornings might start with yoga overlooking paddy fields, followed by brunch at a design-forward cafe, an afternoon at a private beach club, and dinner at a chef-driven restaurant. The community is diverse, creative, and welcoming — a place where Mumbai industrialists, Bangalore tech founders, and European artists mingle naturally.",
      dailyLife: ['Sunrise yoga or beach walks', 'Brunch at Assagao\'s farm-to-fork cafes', 'Afternoon at a private beach club or villa pool', 'Evening drives through Portuguese-era villages', 'Saturday night dinners at Goa\'s best restaurants']
    },
    food: {
      intro: "Goa's food scene is India's most exciting — a fusion of Konkani, Portuguese, and global influences driven by chefs who've moved here from Mumbai, London, and beyond.",
      mustTry: [
        { name: 'Fish Thali', desc: 'Traditional Goan fish curry rice — simple, soulful, unforgettable' },
        { name: 'Prawn Balchao', desc: 'Tangy, spicy prawn pickle — Portuguese-Goan fusion at its best' },
        { name: 'Bebinca', desc: 'Multi-layered coconut pudding — Goa\'s signature dessert' },
        { name: 'Poi Bread', desc: 'Soft, slightly sweet bread baked in traditional wood-fire ovens' },
      ],
      restaurants: [
        { name: 'Jamun', location: 'Assagao', type: 'Fine Dining', specialty: 'Modern Indian with Goan soul — consistently rated Goa\'s best' },
        { name: 'Antares', location: 'Vagator', type: 'Cliff Dining', specialty: 'Sea-facing restaurant by Masterchef Australia winner' },
        { name: 'Bomra\'s', location: 'Candolim', type: 'Asian Fusion', specialty: 'Burmese-inspired cuisine in a garden setting' },
        { name: 'Gunpowder', location: 'Assagao', type: 'South Indian', specialty: 'Kerala and Chettinad in a heritage house' },
      ],
      indianFood: 'Goa is in India — the food ecosystem is exceptional, from local Goan to global cuisine.'
    },
    nightlife: {
      intro: "Goa's nightlife ranges from legendary beach clubs and sunset sessions to intimate jazz bars and underground music events.",
      spots: [
        { name: 'Prana by Sula', location: 'Assagao', type: 'Wine & Dine', vibe: 'Sula Vineyards\' Goa outpost with live music' },
        { name: 'Chronicle', location: 'Anjuna', type: 'Cocktail Bar', vibe: 'Speakeasy-style craft cocktails' },
        { name: 'Thalassa', location: 'Siolim', type: 'Restaurant-Bar', vibe: 'Greek-Goan clifftop dining with sundowner views' },
        { name: 'Curlies', location: 'Anjuna', type: 'Beach Club', vibe: 'Iconic beach shack turned modern party venue' },
        { name: 'Club Cubana', location: 'Arpora', type: 'Nightclub', vibe: 'Hilltop nightclub — Goa\'s original party spot' },
      ]
    },
    attractions: [
      { name: 'Chapora Fort', type: 'Historical', desc: 'The Dil Chahta Hai fort — iconic sunset viewpoint' },
      { name: 'Basilica of Bom Jesus', type: 'Historical', desc: 'UNESCO World Heritage Site, Portuguese Baroque architecture' },
      { name: 'Dudhsagar Falls', type: 'Nature', desc: 'India\'s 5th tallest waterfall amidst lush Western Ghats' },
      { name: 'Divar Island', type: 'Cultural', desc: 'Serene island village with Portuguese-era mansions and churches' },
      { name: 'Saturday Night Market', type: 'Shopping', desc: 'Arpora\'s famous bazaar — global crafts, food, and music' },
    ],
    investorTips: [
      'Assagao-Siolim-Anjuna triangle is the premium belt — highest rental yields',
      'Check for CRZ (Coastal Regulation Zone) compliance before purchasing',
      'South Goa offers larger plots at lower prices but weaker rental demand',
      'Villa management companies handle everything — turnkey rental income possible',
      'New Mopa airport is boosting North Goa property values significantly',
      'RERA-registered projects only — avoid informal land deals',
    ],
    cities: ['Assagao', 'Siolim', 'Anjuna', 'Candolim', 'Vagator', 'Panjim'],
  },
};

// Map country names to destination IDs
const COUNTRY_TO_DEST_ID = {
  'Greece': 'greece',
  'UAE': 'uae',
  'UK': 'uk',
  'Portugal': 'portugal',
  'USA': 'usa',
  'Singapore': 'singapore',
  'Canada': 'canada',
  'Egypt': 'egypt',
  'Alibaug': 'alibaug',
  'Mumbai 3.0': 'mumbai-3.0',
  'Lonavala': 'lonavala',
  'Goa': 'goa',
  'India': 'alibaug',
};

export default function DestinationsPage() {
  const { destinationId } = useParams();
  const navigate = useNavigate();
  const [activeDestination, setActiveDestination] = useState(null);
  const [activeCountries, setActiveCountries] = useState([]);
  const [dbDestinations, setDbDestinations] = useState([]);
  const [dynamicDestDetail, setDynamicDestDetail] = useState(null);

  useEffect(() => {
    getActiveCountries().then(data => setActiveCountries(data.countries || [])).catch(() => {});
    getActiveDestinations().then(data => setDbDestinations(data.destinations || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (destinationId && DESTINATIONS[destinationId]) {
      setActiveDestination(DESTINATIONS[destinationId]);
      setDynamicDestDetail(null);
    } else if (destinationId && destinationId !== 'india' && destinationId !== 'international') {
      setActiveDestination(null);
      getCountryInfo(destinationId).then(data => {
        if (data) setDynamicDestDetail(data);
      }).catch(() => {});
    } else {
      setActiveDestination(null);
      setDynamicDestDetail(null);
    }
  }, [destinationId]);

  // If viewing a hardcoded destination detail
  if (activeDestination) {
    return <DestinationDetail destination={activeDestination} />;
  }

  // If viewing a dynamic (AI-generated) destination detail
  if (dynamicDestDetail) {
    return <DynamicDestinationDetail country={dynamicDestDetail} />;
  }

  // Build available destinations
  const allDests = Object.values(DESTINATIONS);
  const hardcodedIds = new Set(Object.keys(DESTINATIONS));
  const newDbDests = dbDestinations.filter(d => !hardcodedIds.has(d.id) && d.name);
  const allDestinations = [
    ...allDests,
    ...newDbDests.map(d => ({
      id: d.id, name: d.name,
      tagline: d.tagline || `Discover ${d.name}`,
      heroImage: d.image || 'https://images.pexels.com/photos/777059/pexels-photo-777059.jpeg',
      region: d.region || 'Other', isDynamic: true,
    }))
  ];

  const indianDests = allDestinations.filter(d => d.region === 'India');
  const internationalDests = allDestinations.filter(d => d.region !== 'India');

  // Category sub-page: /destinations/india
  if (destinationId === 'india') {
    return <CategorySubPage title="India Collection" subtitle="Luxury villa destinations across India's most sought-after lifestyle locations" dests={indianDests} isIndia />;
  }

  // Category sub-page: /destinations/international
  if (destinationId === 'international') {
    return <CategorySubPage title="International Destinations" subtitle="Residency-by-investment programs across the globe for portfolio diversification" dests={internationalDests} />;
  }

  // Main destinations page — show two category tiles
  return (
    <div className="min-h-screen bg-[#0B0C10] pt-20" data-testid="destinations-page">
      {/* Hero */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/2325446/pexels-photo-2325446.jpeg" alt="Destinations" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B0C10] via-[#0B0C10]/70 to-[#0B0C10]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="label-overline">Destination Guides</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white mt-4 mb-6">
              Discover Your <span className="text-gradient-gold">Second Home</span>
            </h1>
            <p className="text-[#A1A1AA] max-w-3xl mx-auto text-lg">
              Curated lifestyle guides for Indian investors. Explore the culture, cuisine, nightlife,
              and investment potential of our partner destinations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Two Category Tiles */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* India Tile */}
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
              <Link
                to="/destinations/india"
                className="group relative block h-[420px] rounded-2xl overflow-hidden border border-[#2A2F3A] hover:border-[#D4AF37]/60 transition-all"
                data-testid="category-india"
              >
                <img src="https://images.unsplash.com/photo-1757439402277-1a88c7abc89d?w=1600" alt="India" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                <div className="absolute bottom-8 left-8 right-8">
                  <Badge className="bg-emerald-500 text-white mb-3">India</Badge>
                  <h2 className="text-3xl text-white font-semibold mb-2 group-hover:text-[#D4AF37] transition-colors">
                    India Collection
                  </h2>
                  <p className="text-white/70 mb-4">Alibaug, Goa, Lonavala, Mumbai 3.0 — luxury villa destinations within driving distance of Mumbai & Pune</p>
                  <div className="flex items-center text-[#D4AF37] text-sm font-medium">
                    Explore {indianDests.length} Destinations <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-2 transition-transform" />
                  </div>
                </div>
              </Link>
            </motion.div>

            {/* International Tile */}
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
              <Link
                to="/destinations/international"
                className="group relative block h-[420px] rounded-2xl overflow-hidden border border-[#2A2F3A] hover:border-[#D4AF37]/60 transition-all"
                data-testid="category-international"
              >
                <img src="https://images.pexels.com/photos/1285625/pexels-photo-1285625.jpeg" alt="International" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                <div className="absolute bottom-8 left-8 right-8">
                  <Badge className="bg-[#D4AF37] text-[#0B0C10] mb-3">International</Badge>
                  <h2 className="text-3xl text-white font-semibold mb-2 group-hover:text-[#D4AF37] transition-colors">
                    International Destinations
                  </h2>
                  <p className="text-white/70 mb-4">Greece, UAE, UK, Singapore, Portugal & more — residency-by-investment programs across the globe</p>
                  <div className="flex items-center text-[#D4AF37] text-sm font-medium">
                    Explore {internationalDests.length} Destinations <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-2 transition-transform" />
                  </div>
                </div>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0B0C10]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl text-white mb-4">Ready to Find Your Perfect Destination?</h2>
          <p className="text-[#A1A1AA] mb-8">
            Our experts can help you choose the right destination based on your investment goals,
            lifestyle preferences, and family needs.
          </p>
          <Link to="/contact">
            <Button className="btn-luxury px-8">
              Schedule a Consultation <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}

// Category Sub-Page component (India or International)
function CategorySubPage({ title, subtitle, dests, isIndia }) {
  return (
    <div className="min-h-screen bg-[#0B0C10] pt-20" data-testid={`category-page-${isIndia ? 'india' : 'international'}`}>
      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={isIndia ? 'https://images.unsplash.com/photo-1757439402277-1a88c7abc89d?w=1600' : 'https://images.pexels.com/photos/1285625/pexels-photo-1285625.jpeg'}
            alt={title} className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B0C10] via-[#0B0C10]/70 to-[#0B0C10]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/destinations" className="inline-flex items-center text-[#A1A1AA] hover:text-white text-sm mb-6 transition-colors" data-testid="back-to-destinations">
            <ChevronRight className="h-4 w-4 mr-1 rotate-180" /> Back to Destinations
          </Link>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Badge className={`${isIndia ? 'bg-emerald-500 text-white' : 'bg-[#D4AF37] text-[#0B0C10]'} mb-4`}>
              {isIndia ? 'India' : 'International'}
            </Badge>
            <h1 className="text-4xl sm:text-5xl text-white mb-4">{title}</h1>
            <p className="text-[#A1A1AA] max-w-2xl text-lg">{subtitle}</p>
          </motion.div>
        </div>
      </section>

      {/* Destinations Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {dests.map((dest, index) => (
              <motion.div
                key={dest.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.08 }}
                viewport={{ once: true }}
              >
                <Link
                  to={`/destinations/${dest.id}`}
                  className="group block bg-[#1A1D24] border border-[#2A2F3A] rounded-xl overflow-hidden hover:border-[#D4AF37]/50 transition-all"
                  data-testid={`dest-card-${dest.id}`}
                >
                  <div className="relative h-56 overflow-hidden">
                    <img src={dest.heroImage} alt={dest.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <Badge className={`absolute top-4 left-4 ${isIndia ? 'bg-emerald-500/90 text-white' : 'bg-[#D4AF37]/90 text-[#0B0C10]'}`}>
                      {isIndia ? dest.name : (dest.region || 'International')}
                    </Badge>
                  </div>
                  <div className="p-5">
                    <h3 className="text-xl text-white font-medium group-hover:text-[#D4AF37] transition-colors">{dest.name}</h3>
                    <p className="text-[#A1A1AA] text-sm mt-2 line-clamp-2">{dest.tagline}</p>
                    <div className="flex items-center mt-4 text-[#D4AF37] text-sm">
                      {isIndia ? <MapPin className="h-4 w-4 mr-2" /> : <Plane className="h-4 w-4 mr-2" />}
                      {dest.flightFromIndia || (isIndia ? 'Drive from Mumbai/Pune' : 'Direct flights from India')}
                    </div>
                    <div className="flex items-center mt-3 text-[#A1A1AA] text-xs">
                      Read full guide <ArrowRight className="ml-1 h-3 w-3 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0B0C10]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl text-white mb-4">Need Help Choosing?</h2>
          <p className="text-[#A1A1AA] mb-8">Our advisory team can match your investment profile with the ideal destination.</p>
          <Link to="/contact">
            <Button className="btn-luxury px-8">
              Schedule a Consultation <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}

// Dynamic Destination Detail for AI-generated countries
function DynamicDestinationDetail({ country }) {
  const navigate = useNavigate();
  const dest = country.destination_info || {};
  const visa = country.visa_info || {};

  return (
    <div className="min-h-screen bg-[#0B0C10] pt-20">
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src={country.image || 'https://images.pexels.com/photos/777059/pexels-photo-777059.jpeg'} alt={country.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C10] via-black/60 to-black/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button onClick={() => navigate('/destinations')} className="text-[#A1A1AA] hover:text-white mb-4 inline-flex items-center">
            <ChevronRight className="h-4 w-4 mr-1 rotate-180" /> Back to Destinations
          </button>
          <Badge className="bg-[#D4AF37]/10 text-[#D4AF37] border-0 mb-4">{country.region}</Badge>
          <h1 className="text-4xl sm:text-5xl text-white mb-4">{country.name}</h1>
          <p className="text-xl text-white/80 max-w-2xl">{dest.tagline || `Discover investment opportunities in ${country.name}`}</p>
        </div>
      </section>

      <section className="py-16 bg-[#0B0C10]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Quick Facts */}
          {(dest.climate || dest.timezone || dest.flight_from_india) && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-12">
              {[
                { label: 'Climate', value: dest.climate },
                { label: 'Timezone', value: dest.timezone },
                { label: 'Flight from India', value: dest.flight_from_india },
                { label: 'Language', value: dest.language },
              ].filter(f => f.value).map((fact, i) => (
                <div key={i} className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-4 text-center">
                  <p className="text-[#A1A1AA] text-xs mb-1">{fact.label}</p>
                  <p className="text-white font-medium text-sm">{fact.value}</p>
                </div>
              ))}
            </div>
          )}

          {/* Intro */}
          {dest.intro && <p className="text-[#A1A1AA] leading-relaxed text-lg mb-12">{dest.intro}</p>}

          {/* Why Invest */}
          {dest.why_invest?.length > 0 && (
            <div className="mb-12">
              <h2 className="text-2xl text-white mb-6">Why Invest in {country.name}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {dest.why_invest.map((item, i) => (
                  <div key={i} className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-5">
                    <h3 className="text-[#D4AF37] font-medium mb-2">{item.title}</h3>
                    <p className="text-[#A1A1AA] text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Lifestyle */}
          {dest.lifestyle_overview && (
            <div className="mb-12">
              <h2 className="text-2xl text-white mb-4">Lifestyle</h2>
              <p className="text-[#A1A1AA] leading-relaxed">{dest.lifestyle_overview}</p>
            </div>
          )}

          {/* Visa Info */}
          {visa.name && (
            <div className="mb-12 bg-[#1A1D24] border border-[#2A2F3A] rounded-2xl p-6">
              <h2 className="text-2xl text-white mb-4">Visa & Residency Program</h2>
              <h3 className="text-[#D4AF37] font-medium mb-3">{visa.name}</h3>
              {visa.min_investment > 0 && <p className="text-white mb-3">Minimum Investment: <span className="text-[#D4AF37] font-bold">${(visa.min_investment/1000).toFixed(0)}K</span></p>}
              {visa.benefits?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-white text-sm font-medium mb-2">Key Benefits</h4>
                  <ul className="space-y-1">{visa.benefits.map((b, i) => <li key={i} className="text-[#A1A1AA] text-sm flex items-start"><span className="text-[#D4AF37] mr-2 mt-0.5">&#10003;</span>{b}</li>)}</ul>
                </div>
              )}
              {visa.requirements?.length > 0 && (
                <div>
                  <h4 className="text-white text-sm font-medium mb-2">Requirements</h4>
                  <ul className="space-y-1">{visa.requirements.map((r, i) => <li key={i} className="text-[#A1A1AA] text-sm">&#8226; {r}</li>)}</ul>
                </div>
              )}
            </div>
          )}

          {/* CTA */}
          <div className="text-center mt-16">
            <h2 className="text-2xl text-white mb-4">Interested in {country.name}?</h2>
            <Link to="/contact"><Button className="btn-luxury px-8">Schedule a Consultation <ArrowRight className="ml-2 h-5 w-5" /></Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}

// Destination Detail Component
function DestinationDetail({ destination }) {
  const [activeSection, setActiveSection] = useState('overview');

  const sections = [
    { id: 'overview', label: 'Overview', icon: Globe },
    { id: 'lifestyle', label: 'Lifestyle', icon: Heart },
    { id: 'food', label: 'Food & Dining', icon: Utensils },
    { id: 'nightlife', label: 'Nightlife', icon: Wine },
    { id: 'attractions', label: 'Attractions', icon: Camera },
  ];

  return (
    <div className="min-h-screen bg-[#0B0C10] pt-20" data-testid={`destination-${destination.id}`}>
      {/* Hero */}
      <section className="relative h-[60vh] max-h-[600px]">
        <img
          src={destination.heroImage}
          alt={destination.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C10] via-[#0B0C10]/50 to-transparent" />
        
        <div className="absolute bottom-12 left-0 right-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to="/destinations" className="text-[#A1A1AA] hover:text-white mb-4 inline-flex items-center">
              <ChevronRight className="h-4 w-4 mr-1 rotate-180" /> All Destinations
            </Link>
            <Badge className="bg-[#D4AF37] text-[#0B0C10] ml-4">{destination.region}</Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white mt-4 mb-2">
              {destination.name}
            </h1>
            <p className="text-xl text-white/80">{destination.tagline}</p>
          </div>
        </div>
      </section>

      {/* Quick Facts Bar */}
      <section className="bg-[#1A1D24] border-y border-[#2A2F3A] py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-8 text-center">
            <div>
              <div className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-1">Flight Time</div>
              <div className="text-white font-medium">{destination.flightFromIndia}</div>
            </div>
            <div>
              <div className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-1">Best Time</div>
              <div className="text-white font-medium">{destination.bestTime}</div>
            </div>
            <div>
              <div className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-1">Currency</div>
              <div className="text-white font-medium">{destination.currency}</div>
            </div>
            <div>
              <div className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-1">Language</div>
              <div className="text-white font-medium">{destination.language}</div>
            </div>
            <div>
              <div className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-1">Indian Community</div>
              <div className="text-white font-medium">{destination.indianCommunity}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="sticky top-20 z-40 bg-[#0B0C10] border-b border-[#2A2F3A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto py-4 space-x-6 scrollbar-hide">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`flex items-center px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  activeSection === section.id
                    ? 'bg-[#D4AF37] text-[#0B0C10]'
                    : 'text-[#A1A1AA] hover:text-white'
                }`}
              >
                <section.icon className="h-4 w-4 mr-2" />
                {section.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* Introduction */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <p className="text-lg text-[#A1A1AA] leading-relaxed">{destination.intro}</p>
              </motion.div>

              {/* Why Invest */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                <h2 className="text-2xl text-white mb-6 flex items-center">
                  <TrendingUp className="h-6 w-6 text-[#D4AF37] mr-3" />
                  Why Invest in {destination.name}?
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {destination.whyInvest.map((item, index) => (
                    <div key={index} className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-5">
                      <h4 className="text-[#D4AF37] font-medium mb-2">{item.title}</h4>
                      <p className="text-[#A1A1AA] text-sm">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Lifestyle */}
              {activeSection === 'lifestyle' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <h2 className="text-2xl text-white mb-6 flex items-center">
                    <Heart className="h-6 w-6 text-[#D4AF37] mr-3" />
                    Lifestyle & Daily Life
                  </h2>
                  <p className="text-[#A1A1AA] mb-6">{destination.lifestyle.overview}</p>
                  <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-6">
                    <h4 className="text-white font-medium mb-4">What to Expect</h4>
                    <ul className="space-y-3">
                      {(destination.lifestyle?.dailyLife || []).map((item, index) => (
                        <li key={index} className="flex items-center text-[#A1A1AA]">
                          <Sparkles className="h-4 w-4 text-[#D4AF37] mr-3 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              )}

              {/* Food */}
              {activeSection === 'food' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <h2 className="text-2xl text-white mb-6 flex items-center">
                    <Utensils className="h-6 w-6 text-[#D4AF37] mr-3" />
                    Food & Dining
                  </h2>
                  <p className="text-[#A1A1AA] mb-6">{destination.food.intro}</p>
                  
                  <div className="mb-8">
                    <h4 className="text-white font-medium mb-4">Must-Try Dishes</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {(destination.food?.mustTry || []).map((dish, index) => (
                        <div key={index} className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-4">
                          <h5 className="text-[#D4AF37] font-medium">{dish.name}</h5>
                          <p className="text-[#A1A1AA] text-sm mt-1">{dish.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mb-8">
                    <h4 className="text-white font-medium mb-4">Top Restaurants</h4>
                    <div className="space-y-4">
                      {(destination.food?.restaurants || []).map((rest, index) => (
                        <div key={index} className="flex items-center justify-between bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-4">
                          <div>
                            <h5 className="text-white font-medium">{rest.name}</h5>
                            <p className="text-[#A1A1AA] text-sm">{rest.location} • {rest.type}</p>
                          </div>
                          <Badge className="bg-[#D4AF37]/10 text-[#D4AF37] border-0">{rest.specialty}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/30 rounded-xl p-6">
                    <h4 className="text-white font-medium mb-2">Indian Food Availability</h4>
                    <p className="text-[#A1A1AA]">{destination.food.indianFood}</p>
                  </div>
                </motion.div>
              )}

              {/* Nightlife */}
              {activeSection === 'nightlife' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <h2 className="text-2xl text-white mb-6 flex items-center">
                    <Wine className="h-6 w-6 text-[#D4AF37] mr-3" />
                    Nightlife & Entertainment
                  </h2>
                  <p className="text-[#A1A1AA] mb-6">{destination.nightlife.intro}</p>
                  
                  <div className="space-y-4">
                    {(destination.nightlife?.spots || []).map((spot, index) => (
                      <div key={index} className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-5 hover:border-[#D4AF37]/50 transition-colors">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="text-white font-medium">{spot.name}</h4>
                            <p className="text-[#A1A1AA] text-sm">{spot.location}</p>
                          </div>
                          <Badge className="bg-[#1A1D24] text-[#A1A1AA] border border-[#2A2F3A]">{spot.type}</Badge>
                        </div>
                        <p className="text-[#D4AF37] text-sm mt-3">{spot.vibe}</p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Attractions */}
              {activeSection === 'attractions' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <h2 className="text-2xl text-white mb-6 flex items-center">
                    <Camera className="h-6 w-6 text-[#D4AF37] mr-3" />
                    Must-Visit Attractions
                  </h2>
                  
                  <div className="space-y-4">
                    {(destination.attractions || []).map((attr, index) => (
                      <div key={index} className="flex items-start bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-5">
                        <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mr-4 flex-shrink-0">
                          <span className="text-[#D4AF37] font-semibold">{index + 1}</span>
                        </div>
                        <div>
                          <div className="flex items-center gap-3">
                            <h4 className="text-white font-medium">{attr.name}</h4>
                            <Badge className="bg-[#1A1D24] text-[#A1A1AA] border border-[#2A2F3A] text-xs">{attr.type}</Badge>
                          </div>
                          <p className="text-[#A1A1AA] text-sm mt-1">{attr.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Overview (Default) */}
              {activeSection === 'overview' && (
                <>
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <h2 className="text-2xl text-white mb-6 flex items-center">
                      <Heart className="h-6 w-6 text-[#D4AF37] mr-3" />
                      Lifestyle Overview
                    </h2>
                    <p className="text-[#A1A1AA] mb-6">{destination.lifestyle.overview}</p>
                  </motion.div>

                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
                    <h2 className="text-2xl text-white mb-6 flex items-center">
                      <MapPin className="h-6 w-6 text-[#D4AF37] mr-3" />
                      Key Cities
                    </h2>
                    <div className="flex flex-wrap gap-3">
                      {(destination.cities || []).map((city) => (
                        <Link
                          key={city}
                          to={`/properties?search=${encodeURIComponent(city)}`}
                          className="px-4 py-2 bg-[#1A1D24] border border-[#2A2F3A] rounded-full text-white hover:border-[#D4AF37] transition-colors"
                        >
                          {city}
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                </>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-40 space-y-6">
                {/* CTA Card */}
                <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-6">
                  <h3 className="text-lg text-white mb-4">Interested in {destination.name}?</h3>
                  <div className="space-y-3">
                    <Link to={`/properties?search=${encodeURIComponent(destination.name === 'UAE (Dubai)' ? 'Dubai' : destination.name)}`}>
                      <Button className="w-full btn-luxury">
                        <Building2 className="h-4 w-4 mr-2" />
                        View Properties
                      </Button>
                    </Link>
                    <Link to="/contact">
                      <Button variant="outline" className="w-full border-[#2A2F3A] text-white hover:bg-[#2A2F3A]">
                        Schedule Consultation
                      </Button>
                    </Link>
                  </div>
                </div>

                {/* Practical Tips */}
                <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-6">
                  <h4 className="text-white font-medium mb-4">Practical Tips for Indians</h4>
                  <ul className="space-y-3">
                    {(destination.practicalTips || destination.investorTips || []).map((tip, index) => (
                      <li key={index} className="flex items-start text-sm text-[#A1A1AA]">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-2 mr-3 flex-shrink-0" />
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Properties CTA */}
      <section className="py-16 bg-[#1A1D24]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl text-white mb-4">Ready to Invest in {destination.name}?</h2>
          <p className="text-[#A1A1AA] mb-8">
            Browse our curated selection of visa-eligible properties in {destination.name}.
          </p>
          <Link to={`/properties?search=${encodeURIComponent(destination.name === 'UAE (Dubai)' ? 'Dubai' : destination.name)}`}>
            <Button className="btn-luxury px-8">
              View {destination.name} Properties <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
